import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  useInfiniteQuery,
} from "@tanstack/react-query";
import axios from "@/utils/axios";
import type {
  CreateTagPayload,
  UpdateTagPayload,
  ApiTagsResponseType,
  TagType,
} from "@/types/entities/tags";
import { uploadFile } from "../files/file.api";

export interface GetTagsParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  order_by?: string;
  filter?: {
    active?: number;
    search?: string;
  };
  relationships?: string;
}

export const useGetTags = (params?: GetTagsParams) => {
  return useQuery({
    queryKey: ["tags", params],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ApiTagsResponseType> => {
      const response = await axios.get("/admin/v1/tag", { params });
      return response.data;
    },
  });
};

export const useGetTag = (id?: string) => {
  return useQuery({
    queryKey: ["tag", id],
    enabled: !!id,
    queryFn: async (): Promise<{ data: TagType }> => {
      const response = await axios.get(`/admin/v1/tag/${id}`, {
        params: {
          relationships: "processes,files",
        },
      });
      return response.data;
    },
  });
};

export const useGetTagsAsInfinite = (
  filters: GetTagsParams,
  enabled: boolean = true,
) => {
  return useInfiniteQuery({
    queryKey: ["tags-infinite", filters],
    enabled,
    queryFn: async ({ pageParam = 1 }): Promise<ApiTagsResponseType> => {
      const response = await axios.get("/admin/v1/tag", {
        params: {
          ...filters,
          order_by: "!created_at",
          page: pageParam,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (lastPage as ApiTagsResponseType)
        .meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
  });
};

// Interface for tag creation response
interface CreateTagResponse {
  data: {
    id: string;
    name: string;
    description: string;
    active: boolean;
    created_at: string;
    updated_at: string;
  };
}

// Direct-to-GCS upload then attach the resulting upload_id to the tag.
const uploadTagImage = async (
  tagId: string,
  imageFile: File,
): Promise<void> => {
  await uploadFile({
    file: imageFile,
    entity_id: tagId,
    entity_type: "tag",
  });
};

export const useCreateTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTagPayload) => {
      // Step 1: Create the tag with basic data (JSON)
      const tagData = {
        name: payload.name,
        description: payload.description,
        active: payload.active,
      };

      const tagResponse = await axios.post<CreateTagResponse>(
        "admin/v1/tag",
        tagData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      const createdTag = tagResponse.data;
      // Step 2: Upload image if provided
      if (payload.image && createdTag.data.id) {
        try {
          await uploadTagImage(createdTag.data.id, payload.image);
        } catch (imageError) {
          // Log the error with more details
          console.error("Failed to upload tag image:", {
            error: imageError,
            tagId: createdTag.data.id,
            fileName: payload.image.name,
            fileSize: payload.image.size,
          });
          // The tag was created successfully, just without the image
        }
      }

      return createdTag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });
};

export const useUpdateTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateTagPayload;
    }) => {
      // Step 1: Update tag basic data (JSON)
      const updateData: Partial<UpdateTagPayload> = {};

      if (payload.name !== undefined) {
        updateData.name = payload.name;
      }
      if (payload.description !== undefined) {
        updateData.description = payload.description;
      }
      if (payload.active !== undefined) {
        updateData.active = payload.active;
      }

      let tagResponse;
      // Only make the patch request if there's data to update
      if (Object.keys(updateData).length > 0) {
        tagResponse = await axios.patch(`/admin/v1/tag/${id}`, updateData, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
      }

      // Step 2: Upload new image if provided
      if (payload.image) {
        try {
          await uploadTagImage(id, payload.image);
        } catch (imageError) {
          console.warn("Failed to upload updated tag image:", imageError);
          // Continue with the update, just log the image upload failure
        }
      }

      return tagResponse?.data || { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });
};

export const useDeleteTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(`/admin/v1/tag/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });
};

export const useToggleTagStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string;
      action: "activate" | "deactivate";
    }) => {
      const response = await axios.patch(`/admin/v1/tag/${id}/${action}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });
};
